import React, { FC, useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import Label from '../Label';
import TEXT_CLASSNAMES from '../Text/responsiveClassnames';
import {
  WRAPPER_STATES,
  WRAPPER_STATES_DARK,
  BASE_INPUT,
  INPUT_STATES,
  FOCUSED_INPUT,
  ERROR_TEXT,
} from './classnames';
import transition from '../styles/transition';
import { TextFieldProps } from './types';
import TextArea from './TextArea';
import Adornment from './Adornment';

const TextField: FC<TextFieldProps> = ({
  className = '',
  wrapperClassName = '',
  innerClassName = '',
  chat,
  disabled,
  error,
  errorText,
  full,
  hint,
  noBorders,
  check,
  darkMode,
  inputClassName,
  autoComplete,
  autoFocus,
  defaultValue,
  endAdornment,
  adornmentClassNames,
  id,
  inputRef,
  inputProps,
  label,
  labelProps = {},
  maxLength,
  multiline,
  name,
  onChange: onChangeProp,
  onChangeValidation,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  onKeyDown,
  rows = 1,
  rowsMax,
  startAdornment,
  type,
  value,
  white,
  disableAutofillValidation,
  datatestid,
  'data-testid': dataTestId,
  textType = 'body3',
  responsive = 'sm',
  ...other
}) => {
  const inputEl = useRef<HTMLInputElement | HTMLAreaElement>(null);
  const [focused, setFocused] = useState<boolean>(false);
  const [hasContent, setHasContent] = useState<boolean>(
    !!defaultValue || !!value,
  );
  const [isMultirow, setIsMultirow] = useState<boolean>(rows > 1);
  const textClassNames = TEXT_CLASSNAMES[textType]
    ? TEXT_CLASSNAMES[textType](responsive === 'md')
    : '';

  const checkContent = (newValue?: string) => {
    setHasContent(!!(newValue && newValue.length));
  };

  useEffect(() => {
    if (value) {
      checkContent(value.toString());
    } else if (defaultValue) {
      checkContent(defaultValue.toString());
    } else {
      checkContent(undefined);
    }
  }, [value, defaultValue]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    checkContent(e.target.value);

    if (
      typeof onChangeProp === 'function' &&
      (typeof onChangeValidation !== 'function' ||
        onChangeValidation(e.target.value))
    ) {
      onChangeProp(e);
    }
  };

  const onFocus = (e: React.FocusEvent<any>) => {
    setFocused(true);
    if (typeof onFocusProp === 'function') {
      onFocusProp(e);
    }
  };

  const onBlur = (e: React.FocusEvent<any>) => {
    setFocused(false);
    if (typeof onBlurProp === 'function') {
      onBlurProp(e);
    }
  };

  const focusTextField = () => {
    if (inputEl.current) {
      inputEl.current.focus();
    }
  };

  const onAnimationStart = () => {
    if (disableAutofillValidation) {
      setHasContent(true);
    }
  };

  const assignRef = i => {
    if (inputRef && typeof inputRef === 'function') {
      inputRef(i);
    } else if (inputRef) {
      inputRef.current = i;
    }
    inputEl.current = i;
  };

  const onMultirowChange = (newIsMultirow: boolean) => {
    setIsMultirow(newIsMultirow);
  };

  const renderInput = (
    styleState: 'default' | 'check' | 'error' | 'disabled',
  ) => {
    const commonInputProps = {
      autoComplete,
      autoFocus,
      defaultValue: defaultValue ? defaultValue.toString() : undefined,
      disabled,
      id,
      name,
      type,
      value,
      onKeyDown,
      maxLength,
      onChange,
      onFocus,
      onBlur,
      onAnimationStart,
      ...inputProps,
    };

    const applyFocusedStyles = !!label && (focused || hasContent);

    if (multiline) {
      return (
        <TextArea
          data-testid={dataTestId || datatestid}
          inputProps={inputProps}
          styleState={styleState}
          white={white}
          darkMode={darkMode}
          chat={chat}
          noBorders={noBorders}
          inputClassName={inputClassName}
          applyFocusedStyles={applyFocusedStyles}
          areaRef={assignRef}
          rowsMin={rows}
          rowsMax={rowsMax}
          onMultirowChange={onMultirowChange}
          onChange={onChange}
          textClassNames={textClassNames}
          {...commonInputProps}
        />
      );
    }

    return (
      <input
        className={cn(
          {
            [FOCUSED_INPUT({ white: white || darkMode })]: applyFocusedStyles,
            [chat ? 'px-16 py-8' : 'p-16']: !noBorders,
          },
          BASE_INPUT,
          INPUT_STATES[`${styleState}`]({ white: white || darkMode }),
          textClassNames,
          inputClassName,
        )}
        data-testid={dataTestId || datatestid}
        ref={assignRef}
        {...commonInputProps}
      />
    );
  };

  let styleState: 'default' | 'check' | 'error' | 'disabled' = 'default';

  if (check) {
    styleState = 'check';
  }
  if (error || errorText) {
    styleState = 'error';
  }
  if (disabled) {
    styleState = 'disabled';
  }

  const adornmentProps = {
    startAdornment,
    endAdornment,
    adornmentClassNames,
    focusedStyles: !!(label && (focused || hasContent)),
    chat,
    multirow: isMultirow,
  };

  return (
    <div
      className={cn(
        'inline-flex relative',
        { 'pb-20': errorText, 'w-full': full },
        className,
      )}
      {...other}
    >
      <div
        className={cn(
          'inline-flex w-full',
          {
            [darkMode
              ? WRAPPER_STATES_DARK[disabled ? 'disabled' : 'default']
              : WRAPPER_STATES[
                  `${styleState}${focused && !chat ? '-focused' : ''}`
                ]]: !noBorders,
          },
          wrapperClassName,
        )}
        style={{ transition: transition(['background-color', 'border']) }}
      >
        <div className={`flex w-full items-end ${innerClassName}`}>
          {startAdornment && <Adornment type="start" {...adornmentProps} />}
          {label || hint ? (
            <div className="flex w-full relative">
              {label && (
                <Label
                  as="label"
                  label={label}
                  floating
                  focused={focused || hasContent}
                  disabled={disabled}
                  className="select-none z-2 cursor-text"
                  chat={chat}
                  noPadding={noBorders}
                  onClick={focusTextField}
                  darkMode={darkMode}
                  type={textType}
                  responsive={responsive}
                  {...labelProps}
                />
              )}
              {hint && !hasContent && (!label || focused) && (
                <Label
                  as="label"
                  label={hint}
                  disabled={disabled}
                  className={`select-none cursor-text${
                    label && !noBorders ? ' pt-24' : ''
                  }`}
                  chat={chat}
                  noPadding={noBorders}
                  onClick={focusTextField}
                  darkMode={darkMode}
                  type={textType}
                  responsive={responsive}
                  {...labelProps}
                />
              )}
              {renderInput(styleState)}
            </div>
          ) : (
            renderInput(styleState)
          )}
          {check && <Adornment type="check" {...adornmentProps} />}
          {endAdornment && <Adornment type="end" {...adornmentProps} />}
        </div>
        {errorText && (
          <span className={ERROR_TEXT} role="alert">
            {errorText}
          </span>
        )}
      </div>
    </div>
  );
};

export default TextField;
